import { useState } from "react";
import { ActionModal, FIELD_LABEL_CLASS, INPUT_CLASS } from "./ActionModal";
import {
  ACCESS_MODE_DISCLOSURE,
  ACCESS_MODE_DISPLAY,
  ACCESS_NOTE_PROMPT,
  UI_ONLY_ACCESS_EXPLANATION,
} from "../../vocabulary";
import type { AccessFacts, TrackingAccessMode } from "../../types";

const MODES: TrackingAccessMode[] = ["programmatic", "ui-only"];

/**
 * Declare what access Autumn holds to this hotel's GTM container and GA4
 * property (backend issue 48).
 *
 * The fleet's oldest customers granted a seat in their own console rather
 * than the account our automation authenticates as, and nothing we can read
 * back tells the two apart with confidence — so an operator records it, and
 * the note says what they actually hold. Opens on whatever is currently
 * recorded, because most visits here are a correction rather than a first
 * declaration.
 */
export function AccessModeModal({
  access,
  onSubmit,
  onClose,
  pending,
}: {
  access: AccessFacts;
  onSubmit: (body: {
    mode: TrackingAccessMode;
    note?: string;
    reason: string;
  }) => void;
  onClose: () => void;
  pending?: boolean;
}) {
  const [mode, setMode] = useState<TrackingAccessMode>(access.mode);
  const [note, setNote] = useState(access.note ?? "");
  const [reason, setReason] = useState("");
  const uiOnly = mode === "ui-only";

  return (
    <ActionModal
      title="Access to this hotel's tracking"
      submitLabel="Record access"
      onSubmit={() =>
        onSubmit({
          mode,
          // Only ui-only carries a note; the backend nulls it either way, and
          // sending stale prose back on a return to programmatic would put a
          // description of access we no longer hold into the event log.
          note: uiOnly ? note.trim() : undefined,
          reason: reason.trim(),
        })
      }
      onClose={onClose}
      pending={pending}
      disabled={!reason.trim() || (uiOnly && !note.trim())}
    >
      <div>
        <label className={FIELD_LABEL_CLASS}>What we hold</label>
        <select
          className={INPUT_CLASS}
          value={mode}
          onChange={(e) => setMode(e.target.value as TrackingAccessMode)}
        >
          {MODES.map((m) => (
            <option key={m} value={m}>
              {ACCESS_MODE_DISPLAY[m]}
            </option>
          ))}
        </select>
      </div>
      {uiOnly && (
        <div className="rounded bg-warning-light/50 px-3 py-2.5">
          <p className="text-xs text-content-primary">
            {UI_ONLY_ACCESS_EXPLANATION}
          </p>
        </div>
      )}
      {uiOnly && (
        <div>
          <label className={FIELD_LABEL_CLASS}>
            What access we actually have (required)
          </label>
          <textarea
            className={`${INPUT_CLASS} min-h-[66px]`}
            placeholder={ACCESS_NOTE_PROMPT}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-400">{ACCESS_NOTE_PROMPT}</p>
        </div>
      )}
      <div>
        <label className={FIELD_LABEL_CLASS}>Reason (required)</label>
        <textarea
          className={`${INPUT_CLASS} min-h-[66px]`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
      <p className="text-xs text-gray-400">{ACCESS_MODE_DISCLOSURE}</p>
    </ActionModal>
  );
}
